import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AuthContext } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Building2, Globe, Edit2, Trash2, Users, Crown, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_LIMITS = {
  free:       { clients: 1 },
  starter:    { clients: 1 },
  pro:        { clients: 15 },
  agencia:    { clients: 100 },
  agency:     { clients: 100 },
  scale:      { clients: 100 },
  enterprise: { clients: 9999 },
};

export default function Clients() {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    industry: '',
    website: '',
    notes: ''
  });

  const plan = user?.subscription_plan || 'free';
  const planLimit = PLAN_LIMITS[plan]?.clients ?? 0;
  const canAddMore = ['enterprise','scale','agency'].includes(plan) || clients.length < planLimit;

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cliente actualizado');
      } else {
        await axios.post(`${API}/clients`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cliente creado');
      }
      
      setShowModal(false);
      setEditingClient(null);
      setFormData({ name: '', company: '', industry: '', website: '', notes: '' });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cliente');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      company: client.company || '',
      industry: client.industry || '',
      website: client.website || '',
      notes: client.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (clientId) => {
    if (!confirm('¿Estás seguro de eliminar este cliente? Los datos de campañas se mantendrán.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const openNewModal = () => {
    if (!canAddMore) {
      toast.error(`Has alcanzado el límite de ${planLimit} cliente(s) en tu plan. Actualiza para agregar más.`);
      return;
    }
    setEditingClient(null);
    setFormData({ name: '', company: '', industry: '', website: '', notes: '' });
    setShowModal(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-500 text-sm">Cargando clientes...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="clients-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className={`font-heading font-bold text-4xl mb-2 ${theme.text}`}>Clientes</h1>
            <p className={theme.textSecondary}>Gestiona tus clientes y sus campañas</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className={`text-sm ${theme.textSecondary}`}>Plan {user?.subscription_plan?.toUpperCase() || 'FREE'}</p>
              <p className={`text-lg font-bold ${theme.text}`}>{clients.length} / {planLimit} clientes</p>
            </div>
            <Button
              onClick={openNewModal}
              className={`rounded-full h-12 px-6 ${canAddMore ? (theme.isDark ? 'bg-white text-black hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-gray-800') : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              disabled={!canAddMore}
              data-testid="add-client-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nuevo Cliente
            </Button>
          </div>
        </motion.div>

        {/* Plan Upgrade Banner (if no clients allowed) */}
        {false && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${theme.glassCard} p-6 mb-8 border-l-4 border-ai-glow rounded-xl`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-ai-glow/20 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-ai-glow" />
                </div>
                <div>
                  <h3 className={`font-heading font-bold text-lg ${theme.text}`}>Actualiza tu plan para más clientes</h3>
                  <p className={theme.textSecondary}>El plan Free incluye 1 cliente, Starter 1, Growth 15, Scale 100</p>
                </div>
              </div>
              <Button
                onClick={() => window.location.href = '/subscription'}
                className="bg-gradient-to-r from-ai-glow to-brand-meta text-white rounded-full"
              >
                Ver Planes
              </Button>
            </div>
          </motion.div>
        )}

        {/* Clients Grid */}
        {clients.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Users className={`w-16 h-16 ${theme.textSecondary} mx-auto mb-4`} />
            <h3 className={`font-heading font-bold text-xl mb-2 ${theme.text}`}>No tienes clientes aún</h3>
            <p className={`${theme.textSecondary} mb-6`}>Crea tu primer cliente para organizar las campañas</p>
            {canAddMore && (
              <Button
                onClick={openNewModal}
                className={`rounded-full ${theme.isDark ? 'bg-white text-black hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
              >
                <Plus className="w-5 h-5 mr-2" />
                Crear Cliente
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map((client, index) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`${theme.glassCard} p-6 rounded-xl ${theme.cardHover} transition-all`}
                data-testid={`client-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ai-glow/30 to-brand-meta/30 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className={`font-heading font-bold text-lg ${theme.text}`}>{client.name}</h3>
                      {client.company && <p className={`${theme.textSecondary} text-sm`}>{client.company}</p>}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(client)}
                      className={`p-2 rounded-lg ${theme.isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                      data-testid={`edit-client-${index}`}
                    >
                      <Edit2 className={`w-4 h-4 ${theme.text}`} />
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      className={`p-2 rounded-lg ${theme.isDark ? 'bg-white/5 hover:bg-error/20' : 'bg-gray-100 hover:bg-red-100'} hover:text-error transition-colors`}
                      data-testid={`delete-client-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {client.industry && (
                  <p className="text-sm text-secondary mb-2">
                    <span className="text-white/50">Industria:</span> {client.industry}
                  </p>
                )}
                
                {client.website && (
                  <a
                    href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-ai-glow hover:underline"
                  >
                    <Globe className="w-4 h-4" />
                    <span>{client.website}</span>
                  </a>
                )}
                
                {client.notes && (
                  <p className="text-sm text-secondary mt-3 line-clamp-2">{client.notes}</p>
                )}
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => navigate(`/clients/${client.id}`)}
                    variant="outline"
                    className="w-full rounded-full border-white/20 hover:bg-white/10"
                  >
                    Ver cliente →
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-8 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-bold text-xl">
                  {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                    Nombre *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre del cliente"
                    className="bg-white/5 border-white/10 mt-1"
                    required
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                    Empresa
                  </label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Nombre de la empresa"
                    className="bg-white/5 border-white/10 mt-1"
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                    Industria
                  </label>
                  <Input
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    placeholder="Ej: E-commerce, Servicios, etc."
                    className="bg-white/5 border-white/10 mt-1"
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                    Sitio Web
                  </label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="www.ejemplo.com"
                    className="bg-white/5 border-white/10 mt-1"
                  />
                </div>
                
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                    Notas
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notas adicionales..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white placeholder:text-white/30 mt-1 h-24 resize-none"
                  />
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => setShowModal(false)}
                    variant="outline"
                    className="flex-1 rounded-full border-white/20"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-white text-black hover:bg-gray-200 rounded-full"
                  >
                    {editingClient ? 'Guardar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
